import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { onThrowAlert } from "../sagas/dataSaga/dataSagaActions";
import { NotificationModel } from "../reducers/main/mainModel";
import { PersonalAccessTokenProviderInstance } from "../@shared/services/PersonalAccessTokenProvider";
import { PersonalAccessToken } from "../@shared/Models/PersonalAccessToken";

export type UsePersonalAccessTokenResult = {
    onCreateToken: (name: string, isReadOnly: boolean) => Promise<void>;
    onRevokeToken: (id: string) => Promise<void>;
    onLoadTokens: () => Promise<void>;
    isProcessing: boolean;
    isLoading: boolean;
    tokens: PersonalAccessToken[];
    newToken?: string;
}

export const usePersonalAccessToken = (): UsePersonalAccessTokenResult => {
    const dispatch = useDispatch();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tokens, setTokens] = useState<PersonalAccessToken[]>([]);
    const [newToken, setNewToken] = useState<string | undefined>(undefined);

    const onLoadTokens = useCallback(async () => {
        try {
            setIsLoading(true);
            const result = await PersonalAccessTokenProviderInstance.list();
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                setTokens(result.data);
            }
        }
        catch (e) {
            dispatch(onThrowAlert({
                message: (e as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    const onCreateToken = useCallback(async (name: string, isReadOnly: boolean) => {
        try {
            setIsProcessing(true);
            const result = await PersonalAccessTokenProviderInstance.create(name, isReadOnly);
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                setNewToken(result.data);
                onLoadTokens();
            }
        }
        catch (e) {
            dispatch(onThrowAlert({
                message: (e as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsProcessing(false);
        }
    }, [dispatch, onLoadTokens]);

    const onRevokeToken = useCallback(async (id: string) => {
        try {
            setIsProcessing(true);
            const result = await PersonalAccessTokenProviderInstance.revoke(id);
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                onLoadTokens();
            }
        }
        catch (e) {
            dispatch(onThrowAlert({
                message: (e as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsProcessing(false);
        }
    }, [dispatch, onLoadTokens]);

    return {
        onCreateToken,
        onRevokeToken,
        onLoadTokens,
        isProcessing,
        isLoading,
        tokens,
        newToken
    }
}